package org.matthewo

import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.isActive
import kotlin.js.Date

private const val PROFILE = false

class Searcher<T>(entities: List<T>, term: (T) -> String) {
    companion object {
        private const val MAX_SEARCH_RESULTS = 10_000

        private const val MATCH_COST = 0
        private const val SEPERATOR_INSERT_COST = 1
        private const val NON_CASE_MATCH_COST = 3
        private const val INSERT_COST = 5
        private const val REMOVE_COST = 10
        private const val MODIFY_COST = 10

        private val CHAR_INDICES = [
            'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm',
            'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z',
            '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
            '!', '@', '#', '$', '%', '^', '&', '*', '-', '_',
            '.', ',', '|', '/', '?', '"', ':', ';', '(', ')',
            '[', ']', '{', '}', '<', '>', '~'
        ].withIndex().associate { it.value to it.index }

        private val SEPERATOR = setOf('.', '-')
    }

    private val entries = entities.map {
        val term = term(it)
        Entry(term.vector, term, it)
    }.asSequence()
    private val maxSize = entries.maxOfOrNull { it.term.length } ?: 0
    private val costs = Array(maxSize + 1) { IntArray(maxSize + 1) }

    private val String.vector: Long
        get() {
            var v = 0L
            lowercase().forEach {
                v = v or (1L shl (CHAR_INDICES[it] ?: 63))
            }
            return v
        }

    private infix fun Long.characterHits(dst: Long) = (this and dst).countOneBits()

    fun search(term: String, maxMatches: Int = 100, yield: () -> Unit = {}): List<Pair<T, SearchMatch>> {
        val term = term.take(maxSize)

        var cost = 0
        term.forEachIndexed { i, c ->
            cost += if (c in SEPERATOR) SEPERATOR_INSERT_COST else INSERT_COST
            costs[i + 1][0] = cost
        }

        var start = Date.now()
        val vector = term.vector
        val hits = entries.map {
            it to (vector characterHits it.vector)
        }
        val threshold = 0.75 * (hits.maxOfOrNull { it.second } ?: 0)
        if (PROFILE) println("Filter time ${Date.now() - start}")
        start = Date.now()
        val s = hits
            .filter { it.second > threshold }
            .take(MAX_SEARCH_RESULTS)
            .map {
                yield()
                it.first to (term distance it.first.term)
            }
            .sortedBy { it.second.cost / it.first.term.length.toDouble() }
            .take(maxMatches)
            .map {
                it.first.entity to it.second
            }
            .toList()
        if (PROFILE) println("Sort time ${Date.now() - start}")
        return s
    }

    suspend fun searchCancelable(term: String, maxMatches: Int = 100) = coroutineScope {
        search(term, maxMatches) {
            if (!isActive) {
                throw CancellationException()
            }
        }
    }

    private infix fun String.distance(dst: String) = distance(toList(), dst.toList()).let { (cost, matches) ->
        SearchMatch(cost, dst, matches)
    }

    private fun distance(src: List<Char>, dst: List<Char>): Pair<Int, List<IntRange>> {
        val m = src.size
        val n = dst.size

        var cost = 0
        dst.forEachIndexed { j, c ->
            cost += if (c in SEPERATOR) SEPERATOR_INSERT_COST else INSERT_COST
            costs[0][j + 1] = cost
        }

        val srcLower = src.map { it.lowercase() }
        val dstLower = dst.map { it.lowercase() }
        src.forEachIndexed { i, c1 ->
            val l1 = srcLower[i]
            dst.forEachIndexed { j, c2 ->
                costs[i + 1][j + 1] = minOf(
                    costs[i][j + 1] + REMOVE_COST,
                    costs[i + 1][j] + when {
                        c2 in SEPERATOR -> SEPERATOR_INSERT_COST
                        else -> INSERT_COST
                    },
                    costs[i][j] + when {
                        c1 == c2 -> MATCH_COST
                        l1 == dstLower[j] -> NON_CASE_MATCH_COST
                        else -> MODIFY_COST
                    },
                )
            }
        }

        var i = m
        var j = n
        var end = -1
        val matches = mutableListOf<IntRange>()
        fun tryAddMatch() {
            if (-1 != end) matches += j..end
            end = -1
        }
        while (i > 0 && j > 0) {
            val costFromPrevious = costs[i][j] - costs[i - 1][j - 1]
            if (MODIFY_COST == costFromPrevious) {
                tryAddMatch()
                i--
                j--
            } else if (INSERT_COST == costs[i][j] - costs[i][j - 1] || SEPERATOR_INSERT_COST == costs[i][j] - costs[i][j - 1]) {
                tryAddMatch()
                j--
            } else if (REMOVE_COST == costs[i][j] - costs[i - 1][j]) {
                i--
            } else {
                if (-1 == end) {
                    end = j - 1
                }
                i--
                j--
            }
        }
        tryAddMatch()

        return costs[m][n] to matches.reversed()
    }

    private data class Entry<T>(val vector: Long, val term: String, val entity: T)
}

data class SearchMatch(val cost: Int, val term: String, val matches: List<IntRange>)

fun SearchMatch.regions() = sequence {
    if (matches.isEmpty()) {
        yield(false to term)
        return@sequence
    }
    if (matches.first().start > 0) {
        yield(false to term.substring(0, matches.first().start))
    }
    matches.zipWithNext { a, b ->
        yield(true to term.substring(a))
        yield(false to term.substring(a.endInclusive + 1, b.start))
    }
    yield(true to term.substring(matches.last()))
    if (matches.last().endInclusive < term.lastIndex) {
        yield(false to term.substring(matches.last().endInclusive + 1))
    }
}
